import React, { useState, useEffect } from 'react';
import {
  X, User, CreditCard, Receipt, BarChart3, Mail, Phone, MapPin, Calendar,
  CheckCircle, XCircle, DollarSign, Activity, Building, Clock,
  Download, History, ArrowRightLeft, Save, TrendingUp, Package,
  ShieldCheck, ShieldOff
} from 'lucide-react';
import { useMutation, gql } from '@apollo/client';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { transactionsApi } from '../../../api/transactions-api';
import { paymentRequestsApi } from '../../../api/payment-requests-api';
import client from '../../../api/apollo-client';

const GET_REGIONS_WITH_PHARMACIES = gql`
  query GetRegionsWithPharmacies {
    regions {
      id nomRegion
      pharmacies { id namePharmacy }
    }
  }
`;

const GET_PHARMACY_LOGS = gql`
  query GetPharmacyChangeLogs($userId: ID!) {
    pharmacyChangeLogs(userId: $userId) {
      id oldPharmacyName newPharmacyName changedByName reason createdAt
    }
  }
`;

const CHANGE_PHARMACY = gql`
  mutation ChangeUserPharmacy($userId: ID!, $newPharmacyId: ID, $reason: String) {
    changeUserPharmacy(userId: $userId, newPharmacyId: $newPharmacyId, reason: $reason)
  }
`;

const TYPE_LABELS = {
  ADMIN: { label: 'Administrateur', cls: 'bg-purple-100 text-purple-800' },
  PHARMACIST: { label: 'Pharmacien', cls: 'bg-green-100 text-green-800' },
  LABORATORY: { label: 'Laboratoire', cls: 'bg-blue-100 text-blue-800' },
  SELLER: { label: 'Vendeur', cls: 'bg-yellow-100 text-yellow-800' },
  AGENT_TELEPHONIQUE: { label: 'Agent tél.', cls: 'bg-orange-100 text-orange-800' },
  RESPONSABLE_ACHAT: { label: 'Resp. achat', cls: 'bg-pink-100 text-pink-800' },
};

const STATUS_COLORS = {
  transaction: { IN_PROGRESS: 'bg-yellow-100 text-yellow-800', PENDING: 'bg-orange-100 text-orange-800', VALIDATED: 'bg-green-100 text-green-800', CANCELLED: 'bg-red-100 text-red-800' },
  payment: { PENDING: 'bg-yellow-100 text-yellow-800', VALIDATED: 'bg-green-100 text-green-800', CANCELLED: 'bg-red-100 text-red-800' },
};

const Badge = ({ status, type = 'transaction' }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[type][status] || 'bg-gray-100 text-gray-700'}`}>
    {status}
  </span>
);

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  return differenceInDays(new Date(), new Date(dateStr));
};

const UserDetailModal = ({ user, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [userTransactions, setUserTransactions] = useState([]);
  const [userPaymentRequests, setUserPaymentRequests] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingPaymentRequests, setLoadingPaymentRequests] = useState(false);

  // Pharmacy change state
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [selectedPharmacyId, setSelectedPharmacyId] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [pharmacySaved, setPharmacySaved] = useState(false);
  const [regions, setRegions] = useState([]);
  const [pharmacyLogs, setPharmacyLogs] = useState([]);

  useEffect(() => {
    if (!isOpen || regions.length > 0) return;
    client.query({ query: GET_REGIONS_WITH_PHARMACIES, fetchPolicy: 'cache-first' })
      .then(({ data }) => setRegions(data?.regions || []))
      .catch(() => {});
  }, [isOpen]);

  const loadPharmacyLogs = () => {
    if (!user?.id) return;
    client.query({ query: GET_PHARMACY_LOGS, variables: { userId: user.id }, fetchPolicy: 'network-only' })
      .then(({ data }) => setPharmacyLogs(data?.pharmacyChangeLogs || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (isOpen && user?.id) {
      loadPharmacyLogs();
      // Pre-load transactions for stats in general tab
      fetchUserTransactions();
    }
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (activeTab === 'payments' && user && isOpen && userPaymentRequests.length === 0) {
      fetchUserPaymentRequests();
    }
  }, [activeTab, user, isOpen]);

  // Reset state when user changes
  useEffect(() => {
    setActiveTab('general');
    setUserTransactions([]);
    setUserPaymentRequests([]);
    setSelectedRegionId('');
    setSelectedPharmacyId('');
    setChangeReason('');
    setPharmacySaved(false);
  }, [user?.id]);

  const [changePharmacy, { loading: changingPharmacy }] = useMutation(CHANGE_PHARMACY, {
    onCompleted: () => {
      setPharmacySaved(true);
      setChangeReason('');
      setSelectedPharmacyId('');
      setSelectedRegionId('');
      setTimeout(() => setPharmacySaved(false), 2500);
      loadPharmacyLogs();
    },
  });

  const pharmaciesInRegion = selectedRegionId
    ? (regions.find(r => r.id === selectedRegionId)?.pharmacies ?? [])
    : [];

  const fetchUserTransactions = async () => {
    if (!user?.id) return;
    try {
      setLoadingTransactions(true);
      const transactions = await transactionsApi.getTransactionsByUser(user.id);
      setUserTransactions(transactions);
    } catch {
      setUserTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchUserPaymentRequests = async () => {
    if (!user?.id) return;
    try {
      setLoadingPaymentRequests(true);
      const paymentRequests = await paymentRequestsApi.getPaymentRequestsByUser(user.id);
      setUserPaymentRequests(paymentRequests);
    } catch {
      setUserPaymentRequests([]);
    } finally {
      setLoadingPaymentRequests(false);
    }
  };

  const exportUserData = () => {
    try {
      const exportData = {
        user: { email: user.email, name: `${user.firstName} ${user.lastName}`, type: TYPE_LABELS[user.userType]?.label, phone: user.tel, commission: user.commissionBalance, totalTransactions: userTransactions.length, totalEarnings: userTransactions.reduce((sum, t) => sum + (t.gain || 0), 0) },
        transactions: userTransactions.map(t => ({ date: format(new Date(t.date), 'dd/MM/yyyy HH:mm'), product: t.product?.name, amount: t.gain, status: t.status, type: t.type })),
        paymentRequests: userPaymentRequests.map(pr => ({ date: format(new Date(pr.dateDemande), 'dd/MM/yyyy'), amount: pr.amount, status: pr.status, approved: pr.isApproved ? 'Oui' : 'Non' }))
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user_${user.email}_${format(new Date(), 'yyyy-MM-dd')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {}
  };

  if (!isOpen || !user) return null;

  // Derived stats from transactions
  const txByStatus = userTransactions.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || { count: 0, sum: 0 });
    acc[t.status].count++;
    acc[t.status].sum += t.gain || 0;
    return acc;
  }, {});

  const totalEarnings = userTransactions.reduce((s, t) => s + (t.gain || 0), 0);

  // Top products
  const productStats = {};
  userTransactions.forEach(t => {
    if (t.product) {
      if (!productStats[t.product.id]) productStats[t.product.id] = { product: t.product, count: 0, earnings: 0 };
      productStats[t.product.id].count++;
      productStats[t.product.id].earnings += t.gain || 0;
    }
  });
  const topProducts = Object.values(productStats).sort((a, b) => b.count - a.count).slice(0, 5);

  const memberDays = daysSince(user.createdAt);
  const lastLoginDays = daysSince(user.lastLogin);

  // Statistics tab derived data
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const last30 = userTransactions.filter(t => new Date(t.date) >= thirtyDaysAgo);
  const last7 = userTransactions.filter(t => new Date(t.date) >= sevenDaysAgo);
  const monthlyEarnings = last30.reduce((s, t) => s + (t.gain || 0), 0);
  const weeklyEarnings = last7.reduce((s, t) => s + (t.gain || 0), 0);
  const avgPerScan = userTransactions.length > 0 ? totalEarnings / userTransactions.length : 0;

  // Monthly trends (last 6 months)
  const monthlyTrends = Array.from({ length: 6 }, (_, i) => {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - (5 - i) + 1, 0);
    const monthTxs = userTransactions.filter(t => { const d = new Date(t.date); return d >= monthStart && d <= monthEnd; });
    return {
      month: format(monthStart, 'MMM yy', { locale: fr }),
      earnings: monthTxs.reduce((s, t) => s + (t.gain || 0), 0),
      count: monthTxs.length,
    };
  });

  // Hourly activity
  const hourlyActivity = new Array(24).fill(0);
  userTransactions.forEach(t => { hourlyActivity[new Date(t.date).getHours()]++; });
  const maxHourlyCount = Math.max(...hourlyActivity, 1);
  const peakHour = hourlyActivity.indexOf(Math.max(...hourlyActivity));

  const bestMonth = monthlyTrends.length ? monthlyTrends.reduce((best, m) => m.earnings > best.earnings ? m : best) : null;

  const tabs = [
    { id: 'general', label: 'Général', icon: User },
    { id: 'transactions', label: 'Transactions', icon: CreditCard },
    { id: 'payments', label: 'Demandes de paiement', icon: Receipt },
    { id: 'statistics', label: 'Statistiques', icon: BarChart3 },
  ];

  const typeInfo = TYPE_LABELS[user.userType] || { label: user.userType, cls: 'bg-gray-100 text-gray-800' };
  const pharmacy = user.pharmacy || user.ownedPharmacy;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="inline-block w-full max-w-5xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100">
                <User className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">{user.firstName} {user.lastName}</h2>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.cls}`}>
                {typeInfo.label}
              </span>
              {user.isActive ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                  <ShieldCheck size={11} /> Actif
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                  <ShieldOff size={11} /> Inactif
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={exportUserData} className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Exporter
              </button>
              <button onClick={onClose} className="flex items-center justify-center w-8 h-8 text-gray-400 rounded-md hover:text-gray-600 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-6 px-6">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium flex items-center gap-1.5 ${
                    activeTab === id ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[72vh] overflow-y-auto">

            {/* ── GENERAL TAB ── */}
            {activeTab === 'general' && (
              <div className="space-y-5">

                {/* Top info strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-500 mb-0.5">Membre depuis</p>
                    <p className="text-sm font-semibold text-gray-900">{memberDays != null ? `${memberDays} jours` : '—'}</p>
                    {user.createdAt && <p className="text-xs text-gray-400">{format(new Date(user.createdAt), 'dd MMM yyyy', { locale: fr })}</p>}
                  </div>
                  <div className="bg-gray-50 rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-500 mb-0.5">Dernière connexion</p>
                    <p className={`text-sm font-semibold ${lastLoginDays != null && lastLoginDays <= 7 ? 'text-green-700' : lastLoginDays != null && lastLoginDays <= 30 ? 'text-yellow-700' : 'text-red-700'}`}>
                      {lastLoginDays != null ? (lastLoginDays === 0 ? "Aujourd'hui" : lastLoginDays === 1 ? 'Hier' : `Il y a ${lastLoginDays} j`) : 'Jamais'}
                    </p>
                    {user.lastLogin && <p className="text-xs text-gray-400">{format(new Date(user.lastLogin), 'dd MMM yyyy', { locale: fr })}</p>}
                  </div>
                  <div className="bg-gray-50 rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-500 mb-0.5">Tél. vérifié</p>
                    <p className="text-sm font-semibold">{user.isPhoneVerified ? <span className="text-green-700">Oui</span> : <span className="text-orange-600">Non</span>}</p>
                    {user.tel && <p className="text-xs text-gray-400">{user.tel}</p>}
                  </div>
                  <div className="bg-gray-50 rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-500 mb-0.5">Filleuls</p>
                    <p className="text-sm font-semibold text-gray-900">{user.referredUsersCount ?? 0}</p>
                    <p className="text-xs text-gray-400">parrainage</p>
                  </div>
                </div>

                {/* Personal info + Financials */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Personal */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2.5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Informations personnelles</h3>
                    <div className="flex items-center gap-2.5 text-sm">
                      <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-gray-900 truncate">{user.email}</span>
                    </div>
                    {user.tel && (
                      <div className="flex items-center gap-2.5 text-sm">
                        <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-gray-900">{user.tel}</span>
                        {user.isPhoneVerified ? <CheckCircle size={13} className="text-green-500" /> : <XCircle size={13} className="text-red-400" />}
                      </div>
                    )}
                    {user.address && (
                      <div className="flex items-center gap-2.5 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-gray-900">{user.address}</span>
                      </div>
                    )}
                    {user.CIN && (
                      <div className="flex items-center gap-2.5 text-sm">
                        <span className="text-gray-400 text-xs font-bold w-4 shrink-0 text-center">ID</span>
                        <span className="text-gray-900">{user.CIN}</span>
                      </div>
                    )}
                    {user.RIB && (
                      <div className="flex items-center gap-2.5 text-sm">
                        <CreditCard className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-gray-900 font-mono text-xs">{user.RIB}</span>
                      </div>
                    )}
                    {(user.pharmacy?.region || user.ownedPharmacy?.region) && (
                      <div className="flex items-center gap-2.5 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-gray-900">
                          {(user.pharmacy?.region || user.ownedPharmacy?.region)?.nomRegion}
                          {(user.pharmacy?.ville || user.ownedPharmacy?.ville) && ` · ${(user.pharmacy?.ville || user.ownedPharmacy?.ville)?.nomVilleVillage}`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Financial balances */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Finances</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between py-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <DollarSign className="w-4 h-4 text-green-500" /> Solde disponible
                        </div>
                        <span className="text-sm font-bold text-green-700">{(user.commissionBalance || 0).toFixed(2)} DH</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-blue-500" /> Commission approuvée
                        </div>
                        <span className="text-sm font-bold text-blue-700">{(user.approvedCommission || 0).toFixed(2)} DH</span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4 text-yellow-500" /> En attente
                        </div>
                        <span className="text-sm font-bold text-yellow-700">{(user.waitingCommission || 0).toFixed(2)} DH</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity summary cards */}
                {!loadingTransactions && userTransactions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Activité</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-800">{userTransactions.length}</p>
                        <p className="text-xs text-blue-600 mt-0.5">Scans total</p>
                      </div>
                      <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-800">{totalEarnings.toFixed(0)}<span className="text-sm font-normal"> DH</span></p>
                        <p className="text-xs text-green-600 mt-0.5">Gains totaux</p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-yellow-800">{txByStatus['IN_PROGRESS']?.count || 0}</p>
                        <p className="text-xs text-yellow-600 mt-0.5">En cours</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-800">{txByStatus['VALIDATED']?.count || 0}</p>
                        <p className="text-xs text-purple-600 mt-0.5">Validés</p>
                      </div>
                    </div>
                  </div>
                )}
                {loadingTransactions && (
                  <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                    Chargement des statistiques...
                  </div>
                )}

                {/* Top products */}
                {!loadingTransactions && topProducts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Produits les plus scannés</h3>
                    <div className="space-y-2">
                      {topProducts.map((ps, i) => (
                        <div key={ps.product.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                          <span className="flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 rounded-full text-xs font-bold shrink-0">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{ps.product.name}</p>
                            <p className="text-xs text-gray-500">{ps.count} scans</p>
                          </div>
                          <span className="text-sm font-medium text-gray-700 shrink-0">{ps.earnings.toFixed(2)} DH</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pharmacy section */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Pharmacie</h3>

                  {/* Current pharmacy */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-3">
                    <p className="text-xs text-gray-500 mb-0.5">Pharmacie actuelle</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {pharmacy?.namePharmacy ?? <span className="text-gray-400 font-normal">Aucune</span>}
                    </p>
                    {pharmacy?.region && <p className="text-xs text-gray-400 mt-0.5">{pharmacy.region.nomRegion}</p>}
                  </div>

                  {/* Change pharmacy form */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                      <ArrowRightLeft className="w-4 h-4 text-blue-500" /> Changer la pharmacie
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Région</label>
                        <select
                          value={selectedRegionId}
                          onChange={e => { setSelectedRegionId(e.target.value); setSelectedPharmacyId(''); }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Sélectionner une région</option>
                          {regions.map(r => <option key={r.id} value={r.id}>{r.nomRegion}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Pharmacie</label>
                        <select
                          value={selectedPharmacyId}
                          onChange={e => setSelectedPharmacyId(e.target.value)}
                          disabled={!selectedRegionId}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                          <option value="">
                            {selectedRegionId ? (pharmaciesInRegion.length === 0 ? 'Aucune pharmacie' : 'Sélectionner') : '— choisir une région —'}
                          </option>
                          {pharmaciesInRegion.map(p => <option key={p.id} value={p.id}>{p.namePharmacy}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Motif (optionnel)</label>
                      <input
                        type="text"
                        value={changeReason}
                        onChange={e => setChangeReason(e.target.value)}
                        placeholder="Ex : déménagement, correction d'erreur..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      onClick={() => changePharmacy({ variables: { userId: user.id, newPharmacyId: selectedPharmacyId || null, reason: changeReason || null } })}
                      disabled={changingPharmacy || !selectedPharmacyId}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${pharmacySaved ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-200 disabled:text-gray-400'}`}
                    >
                      <Save className="w-4 h-4" />
                      {pharmacySaved ? 'Enregistré !' : changingPharmacy ? 'Enregistrement...' : 'Confirmer'}
                    </button>
                  </div>

                  {/* Change log */}
                  {pharmacyLogs.length > 0 && (
                    <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-3">
                        <History className="w-4 h-4 text-gray-500" /> Historique des changements
                      </div>
                      <div className="space-y-2">
                        {pharmacyLogs.map(log => (
                          <div key={log.id} className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-lg">
                            <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-800">
                                <span className="text-gray-400">{log.oldPharmacyName ?? 'Aucune'}</span>
                                {' → '}
                                <span className="font-medium">{log.newPharmacyName ?? 'Aucune'}</span>
                              </p>
                              {log.reason && <p className="text-xs text-gray-500 mt-0.5">Motif : {log.reason}</p>}
                              <p className="text-xs text-gray-400 mt-0.5">
                                {log.changedByName ? `Par ${log.changedByName} · ` : ''}
                                {format(new Date(log.createdAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── TRANSACTIONS TAB ── */}
            {activeTab === 'transactions' && (
              <div className="space-y-4">
                {/* Status summary */}
                {!loadingTransactions && userTransactions.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { key: 'IN_PROGRESS', label: 'En cours', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800' },
                      { key: 'PENDING', label: 'En attente', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
                      { key: 'VALIDATED', label: 'Validés', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
                      { key: 'CANCELLED', label: 'Annulés', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
                    ].map(({ key, label, bg, border, text }) => (
                      <div key={key} className={`${bg} border ${border} rounded-lg px-4 py-3`}>
                        <p className={`text-xs font-medium ${text} mb-1`}>{label}</p>
                        <p className={`text-xl font-bold ${text}`}>{txByStatus[key]?.count || 0}</p>
                        <p className={`text-xs ${text} opacity-75`}>{(txByStatus[key]?.sum || 0).toFixed(2)} DH</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">Historique des transactions</h3>
                  <span className="text-xs text-gray-500">{userTransactions.length} transaction(s)</span>
                </div>

                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
                    <span className="ml-2 text-sm text-gray-500">Chargement...</span>
                  </div>
                ) : userTransactions.length === 0 ? (
                  <div className="text-center py-10">
                    <CreditCard className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">Aucune transaction</p>
                  </div>
                ) : (
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {['Date', 'Produit', 'Gain', 'Type', 'Statut', 'Label'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {userTransactions.map(t => (
                          <tr key={t.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700">
                              {format(new Date(t.date), 'dd MMM yy HH:mm', { locale: fr })}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-xs font-medium text-gray-900">{t.product?.name || '—'}</div>
                              {t.product?.price && <div className="text-xs text-gray-400">{t.product.price.toFixed(2)} DH</div>}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs font-semibold text-green-600">+{(t.gain || 0).toFixed(2)} DH</div>
                              <div className="text-xs text-gray-400">{(t.companyCommission || 0).toFixed(1)}%</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${t.type === 'SCAN' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                                {t.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <Badge status={t.status} type="transaction" />
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                              {t.label?.code && (
                                <>
                                  <div className="font-mono">{t.label.code}</div>
                                  {t.label.promotion?.name && <div className="text-gray-400">{t.label.promotion.name}</div>}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── PAYMENTS TAB ── */}
            {activeTab === 'payments' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">Demandes de paiement</h3>
                  <span className="text-xs text-gray-500">{userPaymentRequests.length} demande(s)</span>
                </div>

                {loadingPaymentRequests ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
                    <span className="ml-2 text-sm text-gray-500">Chargement...</span>
                  </div>
                ) : userPaymentRequests.length === 0 ? (
                  <div className="text-center py-10">
                    <Receipt className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">Aucune demande de paiement</p>
                  </div>
                ) : (
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {['Date', 'Montant', 'Statut', 'Approuvé', 'Raison refus', 'Organisation'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {userPaymentRequests.map(pr => (
                          <tr key={pr.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700">
                              {format(new Date(pr.dateDemande), 'dd MMM yy HH:mm', { locale: fr })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs font-semibold text-gray-900">{pr.amount.toFixed(2)} DH</td>
                            <td className="px-4 py-3 whitespace-nowrap"><Badge status={pr.status} type="payment" /></td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${pr.isApproved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {pr.isApproved ? 'Oui' : 'Non'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{pr.rejectionReason || '—'}</td>
                            <td className="px-4 py-3 text-xs text-gray-500">{pr.organisme?.nomOrganisme || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── STATISTICS TAB ── */}
            {activeTab === 'statistics' && (
              <div className="space-y-5">
                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
                    <span className="ml-3 text-sm text-gray-500">Calcul des statistiques...</span>
                  </div>
                ) : userTransactions.length === 0 ? (
                  <div className="text-center py-16">
                    <BarChart3 className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">Aucune transaction pour générer des statistiques.</p>
                  </div>
                ) : (
                  <>
                    {/* KPI cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-blue-500" />
                          <span className="text-xs font-medium text-blue-600">Gains totaux</span>
                        </div>
                        <p className="text-xl font-bold text-blue-900">{totalEarnings.toFixed(2)} DH</p>
                      </div>
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Activity className="w-4 h-4 text-green-500" />
                          <span className="text-xs font-medium text-green-600">Total scans</span>
                        </div>
                        <p className="text-xl font-bold text-green-900">{userTransactions.length}</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-purple-500" />
                          <span className="text-xs font-medium text-purple-600">Moy. / scan</span>
                        </div>
                        <p className="text-xl font-bold text-purple-900">{avgPerScan.toFixed(2)} DH</p>
                      </div>
                      <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-orange-500" />
                          <span className="text-xs font-medium text-orange-600">30 derniers jours</span>
                        </div>
                        <p className="text-xl font-bold text-orange-900">{last30.length} scans</p>
                        <p className="text-xs text-orange-600 mt-0.5">{monthlyEarnings.toFixed(0)} DH</p>
                      </div>
                    </div>

                    {/* Period performance */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700">30 derniers jours</h4>
                        <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                          <span className="text-sm text-blue-700">Gains</span>
                          <span className="text-sm font-bold text-blue-900">{monthlyEarnings.toFixed(2)} DH</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                          <span className="text-sm text-green-700">Scans</span>
                          <span className="text-sm font-bold text-green-900">{last30.length}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">Moy. / jour</span>
                          <span className="text-sm font-bold text-gray-900">{(monthlyEarnings / 30).toFixed(2)} DH</span>
                        </div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700">7 derniers jours</h4>
                        <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                          <span className="text-sm text-yellow-700">Gains</span>
                          <span className="text-sm font-bold text-yellow-900">{weeklyEarnings.toFixed(2)} DH</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                          <span className="text-sm text-indigo-700">Scans</span>
                          <span className="text-sm font-bold text-indigo-900">{last7.length}</span>
                        </div>
                        {bestMonth && (
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-600">Meilleur mois</span>
                            <span className="text-sm font-bold text-gray-900">{bestMonth.month} · {bestMonth.earnings.toFixed(0)} DH</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Monthly trends chart */}
                    {monthlyTrends.length > 1 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-4">Évolution sur 6 mois</h4>
                        <div className="w-full overflow-x-auto">
                          {(() => {
                            const maxEarnings = Math.max(...monthlyTrends.map(d => d.earnings), 1);
                            const minEarnings = Math.min(...monthlyTrends.map(d => d.earnings));
                            const range = maxEarnings - minEarnings || 1;
                            const W = 490, H = 160, pad = 35;
                            const points = monthlyTrends.map((m, i) => ({
                              x: pad + (i * (W - 2 * pad)) / (monthlyTrends.length - 1),
                              y: H - pad - ((m.earnings - minEarnings) / range) * (H - 2 * pad),
                              ...m,
                            }));
                            const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                            return (
                              <svg width={W} height={H} className="w-full">
                                {[0, 0.5, 1].map(ratio => {
                                  const val = minEarnings + ratio * range;
                                  const y = H - pad - ratio * (H - 2 * pad);
                                  return (
                                    <g key={ratio}>
                                      <line x1={pad} y1={y} x2={W - pad} y2={y} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 2" />
                                      <text x={pad - 4} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{val.toFixed(0)}</text>
                                    </g>
                                  );
                                })}
                                {points.map((p, i) => (
                                  <text key={i} x={p.x} y={H - 4} textAnchor="middle" fontSize="10" fill="#6b7280">{p.month}</text>
                                ))}
                                <path d={pathD} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                {points.map((p, i) => (
                                  <g key={i}>
                                    <circle cx={p.x} cy={p.y} r="4" fill="#10b981" stroke="white" strokeWidth="2" />
                                    <title>{p.month}: {p.earnings.toFixed(2)} DH ({p.count} scans)</title>
                                  </g>
                                ))}
                              </svg>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Top products + hourly activity */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {topProducts.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Produits les plus scannés</h4>
                          <div className="space-y-2">
                            {topProducts.map((ps, i) => (
                              <div key={ps.product.id} className="flex items-center gap-2.5">
                                <span className="flex items-center justify-center w-5 h-5 bg-green-100 text-green-700 rounded-full text-xs font-bold shrink-0">{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-800 truncate">{ps.product.name}</span>
                                    <span className="text-xs text-gray-500 ml-2 shrink-0">{ps.count} scans · {ps.earnings.toFixed(0)} DH</span>
                                  </div>
                                  <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-400 rounded-full" style={{ width: `${(ps.count / topProducts[0].count) * 100}%` }} />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          Activité par heure · heure de pointe : <span className="text-green-700">{peakHour}h</span>
                        </h4>
                        <div className="flex items-end gap-0.5 h-16">
                          {hourlyActivity.map((count, hour) => (
                            <div key={hour} className="flex-1 flex flex-col items-center justify-end" title={`${hour}h : ${count} scans`}>
                              <div
                                className="w-full rounded-t"
                                style={{
                                  height: `${Math.max((count / maxHourlyCount) * 100, count > 0 ? 4 : 0)}%`,
                                  backgroundColor: count === 0 ? '#f3f4f6' : hour === peakHour ? '#059669' : '#34d399',
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
